import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import type { Task } from "@sales-ops-amplify/shared";
import { isLinkedInTaskType } from "@sales-ops-amplify/shared";
import { getLead } from "@/api/leads";
import { useCompleteTask, useUpdateTask } from "@/hooks/useTasks";
import { useSoftphone } from "@/components/calls/softphone-context";

type Props = {
  task: Task;
  onClose: () => void;
  onUpdated?: (task: Task) => void;
};

function typeLabel(task: Task): string {
  if (task.type === "call") return "Call";
  if (task.type === "manual_email") return "Manual email";
  if (isLinkedInTaskType(task.type)) return "LinkedIn";
  return "Action item";
}

export function TaskWorkbench({ task, onClose, onUpdated }: Props) {
  const navigate = useNavigate();
  const sp = useSoftphone();
  const completeMut = useCompleteTask();
  const updateMut = useUpdateTask();
  const [notes, setNotes] = useState(task.notes ?? task.description ?? "");
  const [copyMsg, setCopyMsg] = useState<string | null>(null);

  const contactQuery = useQuery({
    queryKey: ["contact", task.leadId],
    queryFn: () => getLead(task.leadId!),
    enabled: Boolean(task.leadId),
  });
  const contact = contactQuery.data;

  async function markComplete() {
    const res = await completeMut.mutateAsync({
      taskId: task.taskId,
      notes: notes || undefined,
    });
    onUpdated?.(res.task);
  }

  async function saveNotes() {
    const res = await updateMut.mutateAsync({
      taskId: task.taskId,
      patch: { notes },
    });
    onUpdated?.(res.task);
  }

  async function execute() {
    if (task.type === "call") {
      const phone =
        contact?.mobilePhone || contact?.phone || undefined;
      if (!phone) {
        setCopyMsg("No phone number on contact — open contact to add one.");
        return;
      }
      await sp.dial({
        to: phone,
        leadId: task.leadId,
        leadName: task.leadName ?? contact?.name,
        accountId: task.accountId ?? contact?.accountId,
        opportunityId: task.opportunityId,
        sequenceId: task.sequenceId,
        enrollmentId: task.enrollmentId,
      });
      return;
    }
    if (task.type === "manual_email") {
      const email = contact?.email ?? "";
      const params = new URLSearchParams();
      if (email) params.set("composeTo", email);
      if (task.leadName) params.set("composeName", task.leadName);
      if (task.customMessage || task.notes) {
        params.set(
          "composeBody",
          task.customMessage || task.notes || "",
        );
      }
      if (task.leadId) params.set("leadId", task.leadId);
      navigate(`/emails?${params.toString()}`);
      return;
    }
    if (isLinkedInTaskType(task.type)) {
      const url =
        task.linkedinUrl ||
        contact?.linkedinUrl ||
        "https://www.linkedin.com/";
      window.open(url, "_blank", "noopener,noreferrer");
      const msg = task.customMessage || task.description || "";
      if (msg) {
        try {
          await navigator.clipboard.writeText(msg);
          setCopyMsg("Message copied — paste in LinkedIn");
        } catch {
          setCopyMsg("Opened LinkedIn — copy message manually");
        }
      }
    }
  }

  return (
    <aside className="flex max-h-[calc(100vh-10rem)] flex-col rounded-lg border border-border bg-surface">
      <div className="flex items-start justify-between gap-2 border-b border-border px-4 py-3">
        <div className="min-w-0">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            {typeLabel(task)} · {task.priority}
          </p>
          <h2 className="mt-0.5 truncate text-base font-semibold">{task.title}</h2>
        </div>
        <button
          type="button"
          className="text-sm text-muted-foreground hover:text-foreground"
          onClick={onClose}
        >
          ✕
        </button>
      </div>

      <div className="flex-1 space-y-3 overflow-y-auto px-4 py-3 text-sm">
        <div className="grid gap-1 text-xs">
          <p>
            <span className="text-muted-foreground">Contact · </span>
            {task.leadName || contact?.name || "—"}
            {task.leadId ? (
              <span className="text-muted-foreground">
                {" "}
                ·{" "}
                <Link to="/people" className="text-brand underline">
                  People
                </Link>
              </span>
            ) : null}
          </p>
          <p>
            <span className="text-muted-foreground">Company · </span>
            {task.accountId ? (
              <Link
                to={`/user/accounts/${task.accountId}`}
                className="text-brand underline"
              >
                {task.company || task.accountId}
              </Link>
            ) : (
              task.company || "—"
            )}
          </p>
          {task.opportunityId && (
            <p>
              <span className="text-muted-foreground">Deal · </span>
              <Link
                to={`/deals/${task.opportunityId}`}
                className="text-brand underline"
              >
                {task.dealName || "Deal"}
              </Link>
            </p>
          )}
          {task.sequenceId && (
            <p>
              <span className="text-muted-foreground">Sequence · </span>
              <Link
                to={`/sequences/${task.sequenceId}`}
                className="text-brand underline"
              >
                {task.dealName || "Sequence"}
              </Link>
            </p>
          )}
          <p>
            <span className="text-muted-foreground">Due · </span>
            {new Date(task.dueAt).toLocaleString()}
          </p>
          {task.recommendationReason && (
            <p className="rounded-md bg-brand/10 px-2 py-1 text-brand">
              Recommended: {task.recommendationReason}
            </p>
          )}
        </div>

        {(task.description || task.customMessage) && (
          <div className="rounded-md border border-border/80 bg-background p-2 text-xs">
            <p className="font-medium text-muted-foreground">Instructions</p>
            <p className="mt-1 whitespace-pre-wrap">
              {task.customMessage || task.description}
            </p>
          </div>
        )}

        <label className="block text-xs">
          Notes
          <textarea
            className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm"
            rows={4}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </label>
        {copyMsg && <p className="text-xs text-brand">{copyMsg}</p>}
      </div>

      <div className="flex flex-wrap gap-2 border-t border-border px-4 py-3">
        {task.status === "pending" && (
          <>
            <button
              type="button"
              className="rounded-md bg-brand px-3 py-1.5 text-sm text-white"
              onClick={() => void execute()}
            >
              {task.type === "call"
                ? "Call"
                : task.type === "manual_email"
                  ? "Compose email"
                  : isLinkedInTaskType(task.type)
                    ? "Open LinkedIn"
                    : "Start"}
            </button>
            <button
              type="button"
              className="rounded-md border border-border px-3 py-1.5 text-sm"
              disabled={completeMut.isPending}
              onClick={() => void markComplete()}
            >
              Mark complete
            </button>
          </>
        )}
        <button
          type="button"
          className="rounded-md border border-border px-3 py-1.5 text-sm"
          disabled={updateMut.isPending}
          onClick={() => void saveNotes()}
        >
          Save notes
        </button>
      </div>
    </aside>
  );
}
